// Wraps sessionStorage under one key for "alerts this user has closed this browser session" —
// per the real-time notification spec (DEV-1165 Phase 2 §6), an alert closed by the user stays
// hidden only for the remainder of their active session and reappears at their next login if
// still valid. sessionStorage's own lifetime (cleared on browser/tab close) already IS that
// rule; no expiry logic is needed here.
const STORAGE_KEY = 'mysgl.dismissedAlertIds';

export function getDismissedAlertIds(): Set<number> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw) as number[]) : new Set<number>();
  } catch {
    // Private browsing / storage disabled — dismissal just won't persist, not a hard failure.
    return new Set<number>();
  }
}

export function dismissAlertId(alertId: number): void {
  try {
    const ids = getDismissedAlertIds();
    ids.add(alertId);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Same graceful degradation as above.
  }
}

// A dismissal only means "I closed this specific toast" — it was never meant to survive the
// alert being edited or reactivated (DEV-1165 "Session Storage Eviction for Reactivated &
// Modified Alerts"). Called whenever a fresh AlertUpserted push arrives (see
// LiveAlertsService.applyUpsert), so a stale dismissal from an earlier viewing never silently
// suppresses a genuinely new/changed live alert. A no-op if the id isn't present.
export function evictDismissedAlertId(alertId: number): void {
  try {
    const ids = getDismissedAlertIds();
    if (!ids.delete(alertId)) {
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Same graceful degradation as above.
  }
}
